<template>
  <v-app>
    <router-view />
  </v-app>
</template>

<script>
export default {
  name: "App",
};
</script>

<style>
/* ============================================
   GLOBAL STYLING & THEMING
   ============================================ */

body {
  font-family: "Roboto", "Segoe UI", sans-serif !important;
  background-color: #f5f7fa;
}

html,
body,
#app {
  height: 100%;
  overflow: hidden; /* ❗ Prevent full page scroll */
}

.text-gradient-primary {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;
  -webkit-background-clip: text !important;
  background-clip: text !important;
  -webkit-text-fill-color: transparent !important;
  color: transparent !important;
}

/* ============================================
   BACKGROUND GRADIENTS
   ============================================ */

.bg-gradient-primary {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;
}

.bg-gradient-success {
  background: linear-gradient(135deg, #11998e 0%, #38ef7d 100%) !important;
}

.bg-gradient-info {
  background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%) !important;
}

.bg-gradient-warning {
  background: linear-gradient(135deg, #fa709a 0%, #fee140 100%) !important;
}

.bg-gradient-danger {
  background: linear-gradient(135deg, #eb3349 0%, #f45c43 100%) !important;
}

.bg-gradient-cool {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;
}

.bg-gradient-vibrant {
  background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%) !important;
}

.bg-gradient-ocean {
  background: linear-gradient(135deg, #2e2e78 0%, #662d8c 100%) !important;
}

.bg-gradient-sunset {
  background: linear-gradient(135deg, #ff6b6b 0%, #ffd93d 100%) !important  ;
}

/* ============================================
   CARD UTILITIES
   ============================================ */

.custom-card {
  border-radius: 12px !important;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08) !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
}

.custom-card:hover {
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12) !important;
  transform: translateY(-2px);
}

.custom-card-flat {
  border-radius: 12px !important;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05) !important;
}

.custom-card-title {
  font-weight: 700 !important;
  color: #2c3e50 !important;
  font-size: 1.25rem !important;
  letter-spacing: 0.5px;
}

/* ============================================
   TEXT UTILITIES
   ============================================ */

.text-semibold {
  font-weight: 600 !important;
}

.text-muted {
  color: #888888 !important;
}

.text-sm {
  font-size: 0.9rem !important;
}

.text-xs {
  font-size: 0.75rem !important;
}

.text-xsmall {
  font-size: 0.75rem !important;
}

.text-decorator-none {
  text-decoration: none !important;
}

/* ============================================
   LAYOUT & SPACING UTILITIES
   ============================================ */

.gap-1 {
  gap: 4px;
}

.gap-2 {
  gap: 8px;
}

.gap-3 {
  gap: 12px;
}

.gap-4 {
  gap: 16px;
}

.w-100 {
  width: 100%;
}

.min-h-screen {
  min-height: 100vh;
}

/* ============================================
   BUTTON UTILITIES
   ============================================ */

.btn-primary-gradient {
  background: linear-gradient(90deg, #667eea 0%, #764ba2 100%) !important;
  color: white !important;
}

.btn-primary-gradient:hover {
  box-shadow: 0 8px 20px rgba(102, 126, 234, 0.3) !important;
}

/* ============================================
   DIVIDER UTILITIES
   ============================================ */

.divider-light {
  border-color: #f0f0f0 !important;
}

/* ============================================
   APP BAR STYLING
   ============================================ */

.app-bar-gradient {
  background: linear-gradient(90deg, #ffffff 0%, #f5f7fa 100%) !important;
  border-bottom: 1px solid #e0e0e0 !important;
}

.custom-card-subtitle {
  color: #7f8c8d !important;
  font-weight: 500 !important;
}

/* ============================================
   STATUS BADGES
   ============================================ */

.status-active {
  color: #27ae60 !important;
  font-weight: 700 !important;
  text-transform: uppercase;
  font-size: 0.75rem;
  letter-spacing: 0.5px;
}

.status-inactive {
  color: #e74c3c !important;
  font-weight: 700 !important;
  text-transform: uppercase;
  font-size: 0.75rem;
  letter-spacing: 0.5px;
}

.status-pending {
  color: #f39c12 !important;
  font-weight: 700 !important;
  text-transform: uppercase;
  font-size: 0.75rem;
  letter-spacing: 0.5px;
}

/* ============================================
   STAT CARDS
   ============================================ */

.stat-card {
  border-radius: 12px !important;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1) !important;
  transition: all 0.3s ease;
  position: relative;
}

.stat-card::before {
  content: "";
  position: absolute;
  top: 0;
  right: 0;
  width: 100px;
  height: 100px;
  opacity: 0.1;
  background: radial-gradient(circle, white 0%, transparent 70%);
}

.stat-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 30px rgba(0, 0, 0, 0.15) !important;
}

.stat-value {
  font-size: 2.5rem !important;
  font-weight: 800 !important;
  color: white !important;
}

.stat-label {
  font-size: 0.9rem !important;
  font-weight: 600 !important;
  opacity: 0.9;
  color: white !important;
  text-transform: uppercase;
  letter-spacing: 1px;
}

/* ============================================
   NAVIGATION & LAYOUT
   ============================================ */

.nav-drawer {
  background: linear-gradient(180deg, #f8f9fa 0%, #e9ecef 100%) !important;
}

.app-bar-gradient {
  background: linear-gradient(90deg, #667eea 0%, #764ba2 100%) !important;
}

.nav-item-active {
  background: linear-gradient(90deg, #667eea 0%, #764ba2 100%) !important;
  color: white !important;
}

/* ============================================
   BUTTONS & INPUTS
   ============================================ */

.btn-primary-gradient {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;
  color: white !important;
  border: none !important;
  border-radius: 8px !important;
  font-weight: 600 !important;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  transition: all 0.3s ease !important;
}

.btn-primary-gradient:hover {
  box-shadow: 0 8px 20px rgba(102, 126, 234, 0.4) !important;
}

.input-outlined {
  border-radius: 8px !important;
}

/* ============================================
   CONTAINER & SPACING
   ============================================ */

.container-fluid {
  max-width: 1400px;
  margin: 0 auto;
}

.section-padding {
  padding: 2rem;
}

.section-padding-compact {
  padding: 1.5rem;
}

/* ============================================
   TEXT UTILITIES
   ============================================ */

.text-gradient-primary {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.text-light {
  color: #95a5a6 !important;
}

.text-muted {
  color: #7f8c8d !important;
}

.text-bold {
  font-weight: 700 !important;
}

.text-semibold {
  font-weight: 600 !important;
}

/* ============================================
   DIVIDERS & SEPARATORS
   ============================================ */

.divider-light {
  border-color: #ecf0f1 !important;
}

.divider-gradient {
  border: none;
  height: 3px;
  background: linear-gradient(90deg, #667eea 0%, #764ba2 100%);
}

/* ============================================
   ANIMATIONS & TRANSITIONS
   ============================================ */

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.slide-up-enter-active,
.slide-up-leave-active {
  transition: all 0.3s ease;
}

.slide-up-enter-from {
  transform: translateY(20px);
  opacity: 0;
}

.slide-up-leave-to {
  transform: translateY(-20px);
  opacity: 0;
}

/* ============================================
   RESPONSIVE ADJUSTMENTS
   ============================================ */

@media (max-width: 768px) {
  .stat-value {
    font-size: 2rem !important;
  }

  .section-padding {
    padding: 1rem;
  }

  .login-card {
    width: 100% !important;
  }

  .v-toolbar-title {
    font-size: 1rem !important;
  }
}

/* ============================================
   RESPONSIVE BREAKPOINTS - XS (Extra Small)
   ============================================ */

@media (max-width: 599px) {
  .hide-xs {
    display: none !important;
  }

  .mx-sm-2 {
    margin-left: 4px;
    margin-right: 4px;
  }

  .px-sm-2 {
    padding-left: 4px;
    padding-right: 4px;
  }

  .py-sm-2 {
    padding-top: 4px;
    padding-bottom: 4px;
  }

  .my-4 {
    margin-top: 12px;
    margin-bottom: 12px;
  }

  .padding-compact {
    padding: 8px;
  }
}

/* ============================================
   RESPONSIVE BREAKPOINTS - SM (Small)
   ============================================ */

@media (min-width: 600px) and (max-width: 959px) {
  .hide-sm {
    display: none !important;
  }

  .mx-sm-4 {
    margin-left: 16px;
    margin-right: 16px;
  }

  .compact-form {
    max-width: 100% !important;
  }
}

/* ============================================
   RESPONSIVE BREAKPOINTS - MD (Medium)
   ============================================ */

@media (min-width: 960px) and (max-width: 1263px) {
  .hide-md {
    display: none !important;
  }
}

/* ============================================
   RESPONSIVE BREAKPOINTS - LG (Large) and UP
   ============================================ */

@media (min-width: 1264px) {
  .hide-lg {
    display: none !important;
  }
}

/* ============================================
   RESPONSIVE TEXT UTILITIES
   ============================================ */

@media (max-width: 599px) {
  .text-h4 {
    font-size: 2rem !important;
  }

  .text-h5 {
    font-size: 1.5rem !important;
  }

  .text-h6 {
    font-size: 1rem !important;
  }

  .text-subtitle-1 {
    font-size: 0.9rem !important;
  }

  .text-subtitle-2 {
    font-size: 0.8rem !important;
  }
}

/* ============================================
   RESPONSIVE SPACING UTILITIES
   ============================================ */

@media (max-width: 599px) {
  .my-xs-1 {
    margin-top: 4px;
    margin-bottom: 4px;
  }

  .my-xs-2 {
    margin-top: 8px;
    margin-bottom: 8px;
  }

  .my-xs-3 {
    margin-top: 12px;
    margin-bottom: 12px;
  }

  .my-xs-4 {
    margin-top: 16px;
    margin-bottom: 16px;
  }

  .my-xs-5 {
    margin-top: 20px;
    margin-bottom: 20px;
  }

  .my-xs-6 {
    margin-top: 24px;
    margin-bottom: 24px;
  }

  .pa-xs-2 {
    padding: 8px !important;
  }

  .pa-xs-3 {
    padding: 12px !important;
  }

  .pa-xs-4 {
    padding: 16px !important;
  }
}

/* ============================================
   RESPONSIVE CONTAINER UTILITIES
   ============================================ */

.container-fluid-responsive {
  width: 100%;
  padding: 0;
}

@media (max-width: 599px) {
  .container-fluid-responsive {
    padding: 0 12px;
  }
}

@media (min-width: 600px) and (max-width: 959px) {
  .container-fluid-responsive {
    padding: 0 16px;
  }
}

@media (min-width: 960px) {
  .container-fluid-responsive {
    padding: 0 24px;
  }
}

/* ============================================
   RESPONSIVE CARD UTILITIES
   ============================================ */

.card-responsive {
  width: 100%;
}

@media (max-width: 599px) {
  .card-responsive {
    border-radius: 8px !important;
  }
}

@media (min-width: 600px) {
  .card-responsive {
    border-radius: 12px !important;
  }
}

/* ============================================
   RESPONSIVE GRID UTILITIES
   ============================================ */

@media (max-width: 599px) {
  .grid-cols-1 {
    display: grid;
    grid-template-columns: 1fr;
    gap: 16px;
  }
}

@media (min-width: 600px) and (max-width: 959px) {
  .grid-cols-2 {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
  }
}

@media (min-width: 960px) {
  .grid-cols-3 {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    gap: 16px;
  }

  .grid-cols-4 {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr 1fr;
    gap: 16px;
  }
}

/* ============================================
   RESPONSIVE FLEX UTILITIES
   ============================================ */

.flex-responsive-center {
  display: flex;
  align-items: center;
  justify-content: center;
}

@media (max-width: 599px) {
  .flex-responsive-column {
    flex-direction: column !important;
  }

  .flex-responsive-wrap {
    flex-wrap: wrap !important;
  }
}
</style>
